import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  Drawer,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, ArrowBack as ArrowBackIcon, FilterList as FilterIcon } from '@mui/icons-material';

import { PurchaseRequest, RequestStatus } from '../../types/request.types';
import { useAppSelector } from '../../store';
import { UserRole } from '../../types/auth.types';
import RequestList from './RequestList';
import RequestDetails from './RequestDetails';
import RequestWorkflow from './RequestWorkflow';
import PurchaseRequestForm from './PurchaseRequestForm';
import { requestService } from '../../services/requestService';
import { API_CONFIG } from '../../config/api';

interface RequestsPageProps {
  onBackToDashboard?: () => void;
  mode?: 'my-requests' | 'pending-approvals';
}

interface Department {
  id: string;
  name: string;
}

// Define pending status options
const getPendingStatuses = () => [
  RequestStatus.PENDING_MANAGER,
  RequestStatus.PENDING_SENIOR_MANAGER,
  RequestStatus.PENDING_GENERAL_MANAGER,
  RequestStatus.PENDING_FINANCE,
  RequestStatus.PENDING_CFO,
  RequestStatus.MORE_INFO_NEEDED,
];

const RequestsPage: React.FC<RequestsPageProps> = ({ onBackToDashboard, mode }) => {
  const [selectedRequest, setSelectedRequest] = useState<PurchaseRequest | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('ALL'); // No default filter
  const [departmentFilter, setDepartmentFilter] = useState<string>('ALL');
  const [departments, setDepartments] = useState<Department[]>([]);
  
  const user = useAppSelector((state) => state.auth.user);
  const navigate = useNavigate();

  // Fetch departments for filter
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/users/departments`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setDepartments(data);
        }
      } catch (error) {
        console.error('Failed to fetch departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  const handleBack = useCallback(() => {
    if (onBackToDashboard) {
      onBackToDashboard();
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [onBackToDashboard, navigate]);

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
  };

  const handleDepartmentFilterChange = (value: string) => {
    setDepartmentFilter(value);
  };

  const handleClearFilters = () => {
    setStatusFilter('ALL');
    setDepartmentFilter('ALL');
  };

  const handleViewRequest = (request: PurchaseRequest) => {
    setSelectedRequest(request);
    setIsDrawerOpen(true);
  };

  const handleEditRequest = (request: PurchaseRequest) => {
    setSelectedRequest(request);
    setIsEditing(true);
    setIsFormOpen(true);
  };

  const handleCreateRequest = () => {
    setSelectedRequest(null);
    setIsEditing(false);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedRequest(null);
    setIsEditing(false);
    setIsSubmitting(false);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedRequest(null);
  };

  const handleRequestUpdate = () => {
    // This will trigger a re-fetch of the requests
    setRefreshTrigger(prev => prev + 1);
    setIsDrawerOpen(false);
    setSelectedRequest(null);
  };

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleFormSubmit = async (values: any) => {
    try {
      setIsSubmitting(true);
      
      if (isEditing && selectedRequest) {
        // Handle update logic here if needed
        console.log('Update request:', values);
      } else {
        // Create new request
        console.log('Creating request with values:', values);
        const newRequest = await requestService.createRequest(values);
        console.log('Request created successfully:', newRequest);
      }
      
      handleFormClose();
      // Trigger a refresh of the request list
      triggerRefresh();
    } catch (error) {
      console.error('Error submitting request:', error);
      setIsSubmitting(false);
      // Handle error - you might want to show an alert or toast
      throw error; // Re-throw so the form can handle it
    }
  };

  const getTitle = () => {
    if (!user) return 'Purchase Requests';

    // Handle mode-based titles for roles that support separation
    if (mode && (user.role === UserRole.DEPARTMENT_MANAGER || user.role === UserRole.GENERAL_MANAGER || user.role === UserRole.FINANCE_ADMIN)) {
      switch (mode) {
        case 'my-requests':
          return 'My Purchase Requests';
        case 'pending-approvals':
          switch (user.role) {
            case UserRole.DEPARTMENT_MANAGER:
              return 'Pending Approvals';
            case UserRole.GENERAL_MANAGER:
              return 'Purchase Requests for Final Approval';
            case UserRole.FINANCE_ADMIN:
              return 'Approved Requests for Payment';
            default:
              return 'Pending Approvals';
          }
        default:
          return 'Purchase Requests';
      }
    }

    switch (user.role) {
      case UserRole.EMPLOYEE:
        return 'My Purchase Requests';
      case UserRole.DEPARTMENT_MANAGER:
        return 'Department Purchase Requests';
      case UserRole.SENIOR_MANAGER:
        return 'High-Value Purchase Requests';
      case UserRole.GENERAL_MANAGER:
        return 'Purchase Requests for Final Approval';
      case UserRole.FINANCE_ADMIN:
        return 'Approved Requests for Payment';
      case UserRole.CFO:
        return 'High-Value Financial Requests';
      default:
        return 'Purchase Requests';
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">{getTitle()}</Typography>
          </Box>
          {((user?.role === UserRole.EMPLOYEE) ||
            (user?.role === UserRole.DEPARTMENT_MANAGER && mode === 'my-requests') ||
            (user?.role === UserRole.GENERAL_MANAGER && mode === 'my-requests')) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateRequest}
            >
              New Request
            </Button>
          )}
        </Box>

        {/* Filters Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            <Box sx={{ ml: 'auto' }}>
              <Button size="small" onClick={handleClearFilters}>
                Clear All Filters
              </Button>
            </Box>
          </Box>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <MenuItem value="ALL">All Statuses</MenuItem>
                  <MenuItem value="PENDING">All Pending</MenuItem>
                  <MenuItem value={RequestStatus.DRAFT}>Draft</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_MANAGER}>Pending Manager</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_SENIOR_MANAGER}>Pending Senior Manager</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_GENERAL_MANAGER}>Pending General Manager</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_FINANCE}>Pending Finance</MenuItem>
                  <MenuItem value={RequestStatus.PENDING_CFO}>Pending CFO</MenuItem>
                  <MenuItem value={RequestStatus.MORE_INFO_NEEDED}>More Info Needed</MenuItem>
                  <MenuItem value={RequestStatus.APPROVED}>Approved</MenuItem>
                  <MenuItem value={RequestStatus.REJECTED}>Rejected</MenuItem>
                  <MenuItem value={RequestStatus.PAID}>Paid</MenuItem>
                  <MenuItem value={RequestStatus.CLOSED}>Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => handleDepartmentFilterChange(e.target.value)}
                >
                  <MenuItem value="ALL">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.name}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {statusFilter !== 'ALL' && (
                  <Chip
                    label={`Status: ${statusFilter === 'PENDING' ? 'All Pending' : statusFilter.replace(/_/g, ' ')}`}
                    onDelete={() => setStatusFilter('ALL')}
                    size="small"
                    color="primary"
                  />
                )}
                {departmentFilter !== 'ALL' && (
                  <Chip
                    label={`Department: ${departmentFilter}`}
                    onDelete={() => setDepartmentFilter('ALL')}
                    size="small"
                    color="secondary"
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <RequestList
          title={getTitle()}
          onViewRequest={handleViewRequest}
          onEditRequest={((user?.role === UserRole.EMPLOYEE) ||
            (user?.role === UserRole.DEPARTMENT_MANAGER && mode === 'my-requests') ||
            (user?.role === UserRole.GENERAL_MANAGER && mode === 'my-requests')) ? handleEditRequest : undefined
          }
          refreshTrigger={refreshTrigger}
          statusFilter={statusFilter}
          departmentFilter={departmentFilter}
          mode={mode}
        />
      </Box>

      {/* Request Details Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 500 } },
        }}
      >
        {selectedRequest && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6">Request Details</Typography>
              <IconButton onClick={handleDrawerClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            <RequestDetails
              request={selectedRequest}
              onUpdate={handleRequestUpdate}
            />

            <RequestWorkflow
              request={selectedRequest}
              onStatusChange={handleRequestUpdate}
            />
          </Box>
        )}
      </Drawer>

      {/* Create/Edit Request Dialog */}
      <Dialog
        open={isFormOpen}
        onClose={handleFormClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isEditing ? 'Edit Request' : 'New Purchase Request'}
            </Typography>
            <IconButton onClick={handleFormClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <PurchaseRequestForm
            onSubmit={handleFormSubmit}
            onBackToDashboard={onBackToDashboard}
            initialValues={isEditing && selectedRequest ? {
              title: selectedRequest.title,
              description: selectedRequest.description,
              amount: selectedRequest.amount,
              currency: selectedRequest.currency,
              category: selectedRequest.category,
              urgency: selectedRequest.urgency,
              frequency: selectedRequest.frequency,
              justification: selectedRequest.justification,
              vendorName: selectedRequest.vendorName,
              vendorLink: selectedRequest.vendorLink,
              budgetCode: selectedRequest.budgetCode,
              requestedDeliveryDate: selectedRequest.requestedDeliveryDate,
            } : undefined}
            submitButtonText={isEditing ? 'Update Request' : 'Create Request'}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default RequestsPage; 